/**
 * App Knowledge Service
 * Central service managing the knowledge graph of application capabilities
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import {
  AppKnowledge,
  ActionDefinition,
  EntityDefinition,
  RouteDefinition
} from '../models/app-knowledge.models';
import { DiscoverableFeature } from '../models/feature-discovery.models';
import { ASSISTANT_ACTION_DEFINITIONS } from './assistant-actions.registry';
import { AiAssistApiService } from '../services/ai-assist-api.service';
@Injectable({ providedIn: 'root' })
export class AppKnowledgeService {
  private knowledge$ = new BehaviorSubject<AppKnowledge | null>(null);
  constructor(private apiService: AiAssistApiService) {
    this.loadKnowledge();
  }
  // ============================================================
  // KNOWLEDGE ACCESS
  // ============================================================
  getKnowledge(): Observable<AppKnowledge | null> {
    return this.knowledge$.asObservable();
  }
  getKnowledgeValue(): AppKnowledge | null {
    return this.knowledge$.value;
  }
  getAction(actionId: string): ActionDefinition | undefined {
    return this.knowledge$.value?.actions[actionId];
  }
  getEntity(entityId: string): EntityDefinition | undefined {
    return this.knowledge$.value?.entities[entityId];
  }
  getRoute(path: string): RouteDefinition | undefined {
    return this.knowledge$.value?.routes[path];
  }
  // ============================================================
  // SEARCH & MATCHING
  // ============================================================
  /**
   * Search actions by keywords
   */
  searchActions(query: string): ActionDefinition[] {
    const knowledge = this.knowledge$.value;
    if (!knowledge) return [];
    const queryLower = query.toLowerCase();
    const results: { action: ActionDefinition; score: number }[] = [];
    for (const [actionId, action] of Object.entries(knowledge.actions)) {
      let score = 0;
      // Check display name
      if (action.displayName.toLowerCase().includes(queryLower)) {
        score += 10;
      }
      // Check description
      if (action.description.toLowerCase().includes(queryLower)) {
        score += 5;
      }
      // Check intent phrases
      const intents = knowledge.intents[actionId] || [];
      for (const intent of intents) {
        if (intent.toLowerCase().includes(queryLower)) {
          score += 8;
        }
        if (queryLower.includes(intent.toLowerCase())) {
          score += 8;
        }
      }
      // Check category
      if (action.category.toLowerCase().includes(queryLower)) {
        score += 3;
      }
      if (score > 0) {
        results.push({ action, score });
      }
    }
    return results
      .sort((a, b) => b.score - a.score)
      .map(r => r.action);
  }
  /**
   * Search routes by keywords
   */
  searchRoutes(query: string): RouteDefinition[] {
    const knowledge = this.knowledge$.value;
    if (!knowledge) return [];
    const queryLower = query.toLowerCase();
    const results: { route: RouteDefinition; score: number }[] = [];
    for (const route of Object.values(knowledge.routes)) {
      let score = 0;
      if (route.displayName.toLowerCase().includes(queryLower)) {
        score += 10;
      }
      if (route.description.toLowerCase().includes(queryLower)) {
        score += 5;
      }
      for (const keyword of route.keywords) {
        if (keyword.toLowerCase().includes(queryLower)) {
          score += 7;
        }
      }
      if (score > 0) {
        results.push({ route, score });
      }
    }
    return results
      .sort((a, b) => b.score - a.score)
      .map(r => r.route);
  }
  /**
   * Find best matching action for a user query
   */
  findBestAction(query: string): ActionDefinition | null {
    const matches = this.searchActions(query);
    return matches.length > 0 ? matches[0] : null;
  }
  /**
   * Get all actions for a specific category
   */
  getActionsByCategory(category: string): ActionDefinition[] {
    const knowledge = this.knowledge$.value;
    if (!knowledge) return [];
    return Object.values(knowledge.actions)
      .filter(action => action.category === category);
  }
  /**
   * Get quick actions available on a specific route
   */
  getQuickActionsForRoute(routePath: string): ActionDefinition[] {
    const knowledge = this.knowledge$.value;
    if (!knowledge) return [];
    const route = knowledge.routes[routePath];
    if (!route?.quickActions) return [];
    return route.quickActions
      .map(actionId => knowledge.actions[actionId])
      .filter(Boolean);
  }
  // ============================================================
  // KNOWLEDGE LOADING
  // ============================================================
  private async loadKnowledge(): Promise<void> {
    this.knowledge$.next(this.getDefaultKnowledge());
  }
  /**
   * Reload knowledge (call after app updates)
   */
  async reloadKnowledge(): Promise<void> {
    await this.loadKnowledge();
  }
  /**
   * Default/fallback knowledge if backend unavailable
   * This provides basic navigation even without backend
   */
  private getDefaultKnowledge(): AppKnowledge {
    const knowledge: AppKnowledge = {
      entities: {
        contact: {
          id: 'contact',
          displayName: 'Contact',
          pluralName: 'Contacts',
          description: 'Customer contacts and leads',
          listRoute: '/leads/all-leads',
          detailRoute: '/leads/lead-detail/:id',
          properties: []
        },
        deal: {
          id: 'deal',
          displayName: 'Deal',
          pluralName: 'Deals',
          description: 'Sales opportunities',
          listRoute: '/opportunities/opportunity-list',
          detailRoute: '/opportunities/:id',
          properties: []
        }
      },
      actions: {
        'view-contacts': {
          id: 'view-contacts',
          displayName: 'View Leads',
          description: 'Navigate to leads list',
          category: 'navigation',
          route: '/leads/all-leads',
          sideEffect: 'none',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'view-deals': {
          id: 'view-deals',
          displayName: 'View Opportunities',
          description: 'Navigate to opportunities pipeline',
          category: 'navigation',
          route: '/opportunities/opportunity-list',
          sideEffect: 'none',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'view-dashboard': {
          id: 'view-dashboard',
          displayName: 'View Dashboard',
          description: 'Navigate to dashboard',
          category: 'navigation',
          route: '/home/dashboard',
          sideEffect: 'none',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'leads-page-overview': {
          id: 'leads-page-overview',
          displayName: 'Learn the Leads workspace',
          description: 'Guided tour of filters, grid, and bulk actions',
          category: 'navigation',
          route: '/leads/all-leads',
          workflowId: 'leads-page-overview',
          sideEffect: 'none',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'leads-select-all': {
          id: 'leads-select-all',
          displayName: 'Select all leads for bulk actions',
          description: 'Show how to select the entire filtered list and open bulk actions',
          category: 'data',
          route: '/leads/all-leads',
          workflowId: 'leads-select-all-actions',
          sideEffect: 'none',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'leads-assign': {
          id: 'leads-assign',
          displayName: 'Assign selected leads',
          description: 'Use Quick Assign to route leads to users, teams, or distributions',
          category: 'data',
          route: '/leads/all-leads',
          workflowId: 'leads-assign',
          sideEffect: 'none',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'leads-start-whatsapp': {
          id: 'leads-start-whatsapp',
          displayName: 'Start WhatsApp from Leads list',
          description: 'Select a lead and open the WhatsApp composer',
          category: 'messaging',
          route: '/leads/all-leads',
          workflowId: 'leads-start-whatsapp',
          sideEffect: 'read',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'leads-filter': {
          id: 'leads-filter',
          displayName: 'Filter leads',
          description: 'Apply chips, column filters, and floating filters',
          category: 'data',
          route: '/leads/all-leads',
          workflowId: 'leads-filter',
          sideEffect: 'none',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'leads-delete': {
          id: 'leads-delete',
          displayName: 'Delete or Do Not Track leads',
          description: 'Bulk delete or suppress leads via the delete dialog',
          category: 'data',
          route: '/leads/all-leads',
          workflowId: 'leads-delete',
          sideEffect: 'delete',
          requiresConfirmation: true,
          dangerLevel: 'moderate'
        },
        'leads-edit-deals': {
          id: 'leads-edit-deals',
          displayName: 'Edit deals from leads',
          description: 'Open Create/Update Deal dialog for selected leads',
          category: 'data',
          route: '/leads/all-leads',
          workflowId: 'leads-edit-multiple-deals',
          sideEffect: 'write',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'leads-hide-columns': {
          id: 'leads-hide-columns',
          displayName: 'Hide columns',
          description: 'Use Customize Columns to hide/show any field',
          category: 'navigation',
          route: '/leads/all-leads',
          workflowId: 'leads-hide-columns',
          sideEffect: 'none',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'leads-column-filter-panel': {
          id: 'leads-column-filter-panel',
          displayName: 'Filter columns inside customize panel',
          description: 'Search, filter, and reorder columns within the side panel',
          category: 'navigation',
          route: '/leads/all-leads',
          workflowId: 'leads-column-filter-panel',
          sideEffect: 'none',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'leads-column-chart': {
          id: 'leads-column-chart',
          displayName: 'See a chart for any column',
          description: 'Open the chart icon on a header to visualize data',
          category: 'data',
          route: '/leads/all-leads',
          workflowId: 'leads-column-chart',
          sideEffect: 'read',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'leads-column-search': {
          id: 'leads-column-search',
          displayName: 'Search for a column',
          description: 'Use floating filters or Customize Columns search',
          category: 'navigation',
          route: '/leads/all-leads',
          workflowId: 'leads-column-search',
          sideEffect: 'none',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'leads-smart-views': {
          id: 'leads-smart-views',
          displayName: 'Save or modify Smart Views',
          description: 'Capture current filters/layout and reuse them',
          category: 'navigation',
          route: '/leads/all-leads',
          workflowId: 'leads-smart-views',
          sideEffect: 'write',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'leads-close': {
          id: 'leads-close',
          displayName: 'Manually close leads',
          description: 'Use closing dialog to move leads out of pipeline',
          category: 'data',
          route: '/leads/all-leads',
          workflowId: 'leads-manual-close',
          sideEffect: 'write',
          requiresConfirmation: true,
          dangerLevel: 'moderate'
        },
        'leads-distribution-automation': {
          id: 'leads-distribution-automation',
          displayName: 'Create & attach lead distributions',
          description: 'Build static or rule-based routing and link it to a list or external source.',
          category: 'automation',
          route: '/leads/distributions',
          workflowId: 'leads-distribution-automation',
          sideEffect: 'write',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'leads-export': {
          id: 'leads-export',
          displayName: 'Export filtered leads',
          description: 'Filter, select matching rows, and export the results to CSV/Excel.',
          category: 'export',
          route: '/leads/all-leads',
          workflowId: 'leads-export',
          sideEffect: 'read',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'user-management-overview': {
          id: 'user-management-overview',
          displayName: 'Users & Teams overview',
          description: 'Tour the users screen with buttons and tabs',
          category: 'settings',
          route: '/admin/users',
          workflowId: 'user-management-overview',
          sideEffect: 'none',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'user-invite': {
          id: 'user-invite',
          displayName: 'Invite a new user',
          description: 'Launch the Invite Colleague dialog',
          category: 'settings',
          route: '/admin/users',
          workflowId: 'user-invite',
          sideEffect: 'write',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'user-remove': {
          id: 'user-remove',
          displayName: 'Remove or disable a user',
          description: 'Deactivate temporarily or remove completely',
          category: 'settings',
          route: '/admin/users',
          workflowId: 'user-remove-or-inactivate',
          sideEffect: 'delete',
          requiresConfirmation: true,
          dangerLevel: 'moderate'
        },
        'user-change-number': {
          id: 'user-change-number',
          displayName: 'Change your registered number',
          description: 'Users update their own number in profile',
          category: 'settings',
          route: '/account/profile',
          workflowId: 'user-change-registered-number',
          sideEffect: 'write',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'team-create': {
          id: 'team-create',
          displayName: 'Create a new team',
          description: 'Open the team dialog and assign a manager',
          category: 'settings',
          route: '/admin/users',
          workflowId: 'team-create',
          sideEffect: 'write',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'team-membership': {
          id: 'team-membership',
          displayName: 'Manage team membership',
          description: 'Add or remove members from a team',
          category: 'settings',
          route: '/admin/users',
          workflowId: 'team-membership-manage',
          sideEffect: 'write',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        },
        'user-edit-name-role': {
          id: 'user-edit-name-role',
          displayName: 'Edit user name and role',
          description: 'Open a user record and change display name or authorization level',
          category: 'settings',
          route: '/admin/users',
          workflowId: 'user-edit-name-role',
          sideEffect: 'write',
          requiresConfirmation: false,
          dangerLevel: 'safe'
        }
      },
      routes: {
        '/home/dashboard': {
          path: '/home/dashboard',
          displayName: 'Dashboard',
          description: 'Overview and key metrics',
          keywords: ['home', 'overview', 'main', 'start', 'dashboard'],
          quickActions: ['view-contacts', 'view-deals'],
          discoverableFeatures: []
        },
        '/leads/all-leads': {
          path: '/leads/all-leads',
          displayName: 'Leads',
          description: 'Manage contacts and leads',
          keywords: ['contacts', 'leads', 'people', 'customers'],
          quickActions: ['view-dashboard', 'view-deals'],
          discoverableFeatures: []
        },
        '/leads/bulk-add-new': {
          path: '/leads/bulk-add-new',
          displayName: 'Bulk Upload',
          description: 'Import large lead lists from CSV/Excel',
          keywords: ['bulk', 'upload', 'import leads', 'csv', 'excel'],
          quickActions: [],
          discoverableFeatures: []
        },
        '/opportunities/opportunity-list': {
          path: '/opportunities/opportunity-list',
          displayName: 'Opportunities',
          description: 'Sales pipeline and opportunities',
          keywords: ['deals', 'pipeline', 'sales', 'opportunities'],
          quickActions: ['view-contacts', 'view-dashboard'],
          discoverableFeatures: []
        },
        '/metrics': {
          path: '/metrics',
          displayName: 'Metrics',
          description: 'Analytics and reporting',
          keywords: ['reports', 'analytics', 'metrics', 'data'],
          quickActions: ['view-dashboard', 'view-contacts'],
          discoverableFeatures: []
        },
        '/admin': {
          path: '/admin',
          displayName: 'Admin',
          description: 'Application settings and administration',
          keywords: ['settings', 'preferences', 'configuration', 'options', 'admin'],
          quickActions: ['view-dashboard'],
          discoverableFeatures: [
          ]
        }
      },
      intents: {
        'view-contacts': ['go to contacts', 'show contacts', 'open contacts', 'view contacts', 'go to leads', 'show leads', 'view leads', 'all leads'],
        'view-deals': ['go to deals', 'show deals', 'open deals', 'view deals', 'pipeline', 'go to opportunities', 'show opportunities', 'view opportunities'],
        'view-dashboard': ['go to dashboard', 'show dashboard', 'go home', 'home'],
        'leads-assign': ['assign leads', 'bulk assign', 'reassign leads', 'distribute leads', 'assign to user', 'assign to team'],
        'leads-start-whatsapp': ['whatsapp campaign', 'send whatsapp', 'whatsapp broadcast', 'bulk whatsapp', 'message leads'],
        'leads-filter': ['filter leads', 'search leads', 'advanced filter', 'find leads', 'filter contacts'],
        'leads-delete': ['delete leads', 'remove leads', 'bulk delete', 'delete contacts'],
        'leads-edit-deals': ['edit deal', 'create deal from lead', 'update deal', 'convert to deal'],
        'leads-hide-columns': ['hide columns', 'show columns', 'customize table', 'customize grid', 'column visibility'],
        'leads-smart-views': ['smart view', 'save view', 'create smart view', 'saved view', 'modify view'],
        'leads-close': ['close lead', 'close leads', 'mark closed', 'change category', 'mark won', 'mark lost', 'not interested'],
        'leads-distribution-automation': ['distribution', 'lead distribution', 'auto assign', 'routing rule', 'round robin'],
        'leads-export': ['export leads', 'download leads', 'export csv', 'export excel', 'download data'],
        'leads-select-all': ['select all', 'bulk select', 'select all leads', 'bulk actions'],
        'user-invite': ['invite user', 'add user', 'invite team member', 'add employee', 'invite colleague'],
        'user-remove': ['remove user', 'delete user', 'deactivate user', 'remove employee'],
        'team-create': ['create team', 'new team', 'add team', 'team setup'],
        'team-membership': ['add team member', 'manage team', 'team members', 'assign to team'],
        'user-edit-name-role': ['edit user', 'change role', 'update user name', 'edit profile'],
        'user-management-overview': ['manage users', 'user management', 'users and teams'],
        'leads-page-overview': ['leads page', 'leads overview', 'explore leads'],
        'leads-column-chart': ['column chart', 'chart for column', 'visualize column'],
        'leads-column-filter-panel': ['column filter', 'filter panel', 'filter columns'],
        'leads-column-search': ['search column', 'find column', 'column search'],
        'log-call': ['log call', 'call outcome', 'call log', 'add call outcome', 'record call', 'call disposition', 'log outcome', 'manual call log'],
        'bulk-upload': ['bulk upload', 'upload leads', 'import csv', 'import excel', 'upload file'],
        'create-deal': ['create deal', 'new deal', 'add deal', 'add opportunity'],
        'customize-grid': ['customize grid', 'grid columns', 'table layout', 'customize table']
      }
    };
    this.applyDiscoverableFeatures(knowledge);
    return knowledge;
  }
  private applyDiscoverableFeatures(knowledge: AppKnowledge): void {
    const featureMap = this.buildDiscoverableFeaturesMap();
    for (const [route, features] of Object.entries(featureMap)) {
      if (!knowledge.routes[route]) {
        // Auto-create route so registry-driven discoverables are not silently dropped
        knowledge.routes[route] = {
          path: route,
          displayName: route.split('/').filter(Boolean).pop() || route,
          description: '',
          keywords: [],
          quickActions: [],
          discoverableFeatures: []
        };
      }
      knowledge.routes[route].discoverableFeatures = features;
    }
  }
  private buildDiscoverableFeaturesMap(): Record<string, DiscoverableFeature[]> {
    const map: Record<string, DiscoverableFeature[]> = {};
    for (const action of ASSISTANT_ACTION_DEFINITIONS) {
      if (!action.discoverable) {
        continue;
      }
      const cfg = action.discoverable;
      const route = cfg.route;
      if (!route) {
        continue;
      }
      const feature: DiscoverableFeature = {
        id: cfg.id ?? `${action.id}-discoverable`,
        title: cfg.title ?? action.text,
        description: cfg.description ?? action.tooltip,
        icon: cfg.icon ?? action.icon ?? 'auto_awesome',
        workflowId: cfg.workflowId ?? action.workflowId,
        videoUrl: cfg.videoUrl,
        actionId: cfg.actionId ?? action.id,
        targetSelector: cfg.targetSelector,
        priority: cfg.priority ?? 50,
        showOnce: cfg.showOnce,
        requiredRole: cfg.requiredRoles,
        route
      };
      if (!map[route]) {
        map[route] = [];
      }
      map[route].push(feature);
    }
    Object.values(map).forEach(features =>
      features.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
    );
    return map;
  }
}
